import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IMAGES, API } from "@/lib/site";

const SAMPLES = [
  { tag: "Health", title: "The 4 metrics that actually predict your energy", read: "6 min read", image: IMAGES.wellness, slug: null },
  { tag: "Wealth", title: "Why your money anxiety isn't about money", read: "5 min read", image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", slug: null },
  { tag: "Mindset", title: "A morning protocol for a resilient mind", read: "4 min read", image: IMAGES.texture, slug: null },
];

const CATEGORIES = ["All", "Health", "Wealth", "Mindset", "Relationships"];

export const Journal = () => {
  const [posts, setPosts] = useState(SAMPLES);
  const [isReal, setIsReal] = useState(false);
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/blog`)
      .then((r) => r.json())
      .then((d) => {
        if (d.posts?.length) {
          setPosts(d.posts);
          setIsReal(true);
        }
      })
      .catch(() => {});
  }, []);

  const open = (p) => {
    if (p.slug) navigate(`/blog/${p.slug}`);
  };

  const filtered = filter === "All" ? posts : posts.filter((p) => (p.tag || "").toLowerCase() === filter.toLowerCase());

  return (
    <section id="journal" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="overline">The journal</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-brand-text mt-4">
            Notes from the practice.
          </h2>
        </div>
        <button
          data-testid="journal-view-all"
          onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
          className="text-sm text-brand-gold hover:text-brand-text transition-colors self-start md:self-auto"
        >
          Get new essays by email →
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-12">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`journal-filter-${c.toLowerCase()}`}
            onClick={() => setFilter(c)}
            className={`rounded-full px-5 py-2 text-sm transition-colors duration-300 ${
              filter === c ? "bg-brand-gold text-brand-bg" : "border border-brand-line text-brand-dim hover:text-brand-text hover:border-brand-gold"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-brand-dim">No posts in this category yet.</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-8">
          {filtered.slice(0, 6).map((p, i) => (
            <motion.article
              key={p.slug || p.title}
              data-testid={`journal-post-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => open(p)}
              className={`group ${isReal ? "cursor-pointer" : ""}`}
            >
              <div className="overflow-hidden rounded-2xl border border-brand-line bg-brand-surface">
                <img
                  src={p.image || IMAGES.texture}
                  alt={p.title}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="pt-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="overline">{p.tag}</span>
                  <span className="text-xs text-brand-dim">· {p.read}</span>
                </div>
                <h3 className="font-serif text-2xl text-brand-text group-hover:text-brand-gold transition-colors duration-300 leading-snug">
                  {p.title}
                </h3>
                {p.excerpt && <p className="text-sm text-brand-dim mt-2 line-clamp-2">{p.excerpt}</p>}
              </div>
            </motion.article>
          ))}
        </div>
      )}
    </section>
  );
};
